'use client';

import Link from 'next/link';
import type { BillingPeriod, Tier } from './types';

interface Props {
  tier: Tier;
  billing: BillingPeriod;
  isAuthenticated?: boolean;
  isCurrentTier?: boolean;
  isTrialUpgrade?: boolean;
  activating?: boolean;
  ctaFootnote?: string;
  onChoose?: () => void;
}

function formatINR(value: number): string {
  return `₹${value.toLocaleString('en-IN')}`;
}

function loginHrefFor(tier: Tier, billing: BillingPeriod): string {
  if (tier.id === 'firm') {
    return `/login?redirect=${encodeURIComponent(`/team/new?billing=${billing}`)}`;
  }

  return `/login?redirect=${encodeURIComponent(
    `/pricing?tier=${tier.id}&billing=${billing}&checkout=1`
  )}`;
}

export default function TierCard({
  tier,
  billing,
  isAuthenticated,
  isCurrentTier,
  isTrialUpgrade,
  activating,
  ctaFootnote,
  onChoose,
}: Props) {
  const isFirm = tier.id === 'firm';
  const isCorporate = tier.id === 'corporate';
  const isComingSoon = tier.ctaLabel === 'Coming Soon';
  const effectiveBilling = tier.isYearlyOnly ? 'yearly' : billing;
  const price = tier.isYearlyOnly && billing === 'monthly' && tier.monthlyEquivalentInr
    ? tier.monthlyEquivalentInr
    : effectiveBilling === 'monthly'
      ? tier.priceMonthly
      : tier.priceYearly;
  const priceSuffix = tier.isYearlyOnly && billing === 'monthly' ? '/mo' : effectiveBilling === 'monthly' ? '/mo' : '/yr';
  const seatSuffix = isFirm ? ' per seat' : '';

  const monthlyAnnualized = (tier.priceMonthly || 0) * 12;
  const yearlySavings = tier.priceYearly ? monthlyAnnualized - tier.priceYearly : 0;

  const firmMinMonthly = (tier.seatMinimum ?? 1) * (tier.priceMonthly || 0);
  const firmMinYearly = (tier.seatMinimum ?? 1) * (tier.priceYearly || 0);
  const selectLabel = isTrialUpgrade ? 'Upgrade now' : 'Select';

  const cardBorder = tier.isPopular
    ? 'border-indigo-500 ring-2 ring-indigo-500/10 shadow-lg shadow-indigo-500/10'
    : 'border-slate-200';
  const selectableCtaClass = tier.isPopular
    ? 'block w-full rounded-lg bg-gradient-to-r from-indigo-600 to-violet-600 px-4 py-3 text-center text-sm font-semibold text-white shadow-sm transition-shadow hover:shadow-md'
    : 'block w-full rounded-lg bg-slate-900 px-4 py-3 text-center text-sm font-semibold text-white shadow-sm transition-colors hover:bg-slate-800';

  // Determine CTA rendering
  let ctaElement: React.ReactNode;

  if (isComingSoon) {
    ctaElement = (
      <span className="block w-full rounded-lg bg-slate-100 px-4 py-2.5 text-center text-sm font-semibold text-slate-400 cursor-not-allowed">
        Coming Soon
      </span>
    );
  } else if (isCurrentTier) {
    ctaElement = (
      <button
        type="button"
        disabled
        className="block w-full cursor-not-allowed rounded-lg bg-slate-100 px-4 py-2.5 text-center text-sm font-semibold text-slate-500"
      >
        Current Plan
      </button>
    );
  } else if (tier.requestAccessOnly || isCorporate) {
    ctaElement = (
      <Link
        href="/request-access"
        className={selectableCtaClass}
      >
        {selectLabel}
      </Link>
    );
  } else if (!isAuthenticated) {
    // Not logged in — direct to login first
    ctaElement = (
      <Link
        href={loginHrefFor(tier, effectiveBilling)}
        className={selectableCtaClass}
      >
        {selectLabel}
      </Link>
    );
  } else {
    // Authenticated — trigger Razorpay checkout
    ctaElement = (
      <button
        type="button"
        onClick={onChoose}
        disabled={activating}
        className={`${selectableCtaClass} disabled:cursor-not-allowed disabled:opacity-50`}
      >
        {activating ? 'Opening...' : selectLabel}
      </button>
    );
  }

  return (
    <div
      className={`relative flex flex-col rounded-lg border bg-white p-7 transition-shadow ${cardBorder} ${
        tier.isPopular ? 'lg:-translate-y-2' : ''
      }`}
    >
      {tier.isPopular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 px-3 py-1 text-[11px] font-semibold text-white shadow-sm">
            Best Value
          </span>
        </div>
      )}

      <div className="mb-1">
        <h3 className="text-lg font-semibold text-slate-900">{tier.name}</h3>
        <p className="mt-1 text-sm text-slate-500">{tier.sub}</p>
      </div>

      {price ? (
        <div className="mt-6 flex items-baseline gap-1">
          <span className="text-4xl font-bold text-slate-900">
            {formatINR(price)}
          </span>
          <span className="text-sm text-slate-500">
            {priceSuffix}
            {seatSuffix}
          </span>
        </div>
      ) : (
        <div className="mt-6">
          <span className="text-4xl font-bold text-slate-900">Custom</span>
          <p className="mt-1 text-sm text-slate-500">Request access for pricing</p>
        </div>
      )}

      {!isCorporate && (
        <p className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-medium text-slate-600">
          <svg className="h-3 w-3 text-emerald-500" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          All inclusive · GST &amp; no hidden fees
        </p>
      )}

      {tier.isYearlyOnly && tier.priceYearly && (
        <p className="mt-1 text-xs font-medium text-slate-600">
          Billed yearly at {formatINR(tier.priceYearly)}
        </p>
      )}

      {billing === 'yearly' && yearlySavings > 0 && (
        <p className="mt-1 text-xs font-medium text-emerald-600">
          Save {formatINR(yearlySavings)}{isFirm ? '/seat' : ''} a year
        </p>
      )}

      {isFirm && (
        <p className="mt-2 rounded-md bg-slate-50 px-2.5 py-1.5 text-[11px] font-medium leading-relaxed text-slate-600">
          {tier.seatMinimum}-seat minimum · Public starting price:{' '}
          <strong className="text-slate-900">{formatINR(firmMinMonthly)}/month minimum</strong>
          {' · '}
          <strong className="text-slate-900">{formatINR(firmMinYearly)}/year minimum</strong>
        </p>
      )}

      {/* CTA */}
      <div className="mt-6">{ctaElement}</div>
      {ctaFootnote && (
        <p className="mt-2 text-center text-xs font-medium text-emerald-700">{ctaFootnote}</p>
      )}

      {/* Feature list */}
      <ul className="mt-6 space-y-2.5">
        {tier.cardFeatures.map((feature) => (
          <li key={feature} className="flex items-start gap-2.5 text-sm text-slate-700">
            <svg
              className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-500"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <span>{feature}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
